/*
  Tearstones jewelry description

  Finished-jewelry descriptions are written as an intro paragraph, a paragraph of
  "Label: value" lines and a short note. This component lays the label lines out as a
  spec sheet and marks the intro and note. Product copy, links and value markup stay
  the Shopify description's original DOM nodes; they are only moved.
*/

class TearstonesJewelryDescription extends HTMLElement {
  connectedCallback() {
    if (!this.closest("[data-jewelry-product]")) return;
    this.labelListItems();
    this.buildSpecSheet();
  }

  labelListItems() {
    this.querySelectorAll(".rte > ul > li, .rte > ol > li").forEach((item) => {
      const firstNode = item.firstChild;
      if (firstNode?.nodeType !== Node.TEXT_NODE) return;

      const prefix = firstNode.data.match(/^(\s*[^:\r\n]{1,40}:)(?=\s|$)/u)?.[1];
      if (!prefix || !prefix.slice(0, -1).trim()) return;

      const label = document.createElement("strong");
      label.className = "ts-jewelry-description-label";
      firstNode.splitText(prefix.length);
      item.insertBefore(label, firstNode);
      label.appendChild(firstNode);
      item.classList.add("ts-jewelry-description-row");
    });
  }

  buildSpecSheet() {
    const rte = this.querySelector(".rte");
    if (!rte || rte.classList.contains("ts-spec-ready")) return;

    let sheet = null;
    for (const paragraph of rte.querySelectorAll(":scope > p")) {
      const rows = this.readRows(paragraph);
      if (!rows) continue;

      sheet = document.createElement("dl");
      sheet.className = "ts-spec-grid";
      rows.forEach(({ label, nodes }) => {
        const row = document.createElement("div");
        row.className = "ts-spec-row";
        const term = document.createElement("dt");
        term.textContent = label;
        const detail = document.createElement("dd");
        nodes.forEach((node) => detail.appendChild(node));
        if (detail.firstChild?.nodeType === Node.TEXT_NODE) {
          detail.firstChild.data = detail.firstChild.data.replace(/^\s+/u, "");
        }
        if (detail.textContent.trim().length > 32) row.classList.add("ts-spec-row--wide");
        row.append(term, detail);
        sheet.appendChild(row);
      });
      paragraph.replaceWith(sheet);
      break;
    }
    if (!sheet) return;

    rte.querySelectorAll(":scope > p").forEach((paragraph) => {
      const before = paragraph.compareDocumentPosition(sheet) & Node.DOCUMENT_POSITION_FOLLOWING;
      paragraph.classList.add(before ? "ts-spec-lead" : "ts-spec-note");
    });
    rte.classList.add("ts-spec-ready");
  }

  /* Returns [{label, nodes}] when every line of the paragraph is "<strong>Label:</strong> value". */
  readRows(paragraph) {
    const lines = [[]];
    paragraph.childNodes.forEach((node) => {
      if (node.nodeName === "BR") lines.push([]);
      else lines[lines.length - 1].push(node);
    });

    const rows = [];
    for (const line of lines) {
      const nodes = line.filter((node) => !(node.nodeType === Node.TEXT_NODE && !node.data.trim()));
      if (!nodes.length) continue;
      const head = nodes[0];
      if (head.nodeName !== "STRONG" || !/:\s*$/u.test(head.textContent)) return null;
      const label = head.textContent.replace(/:\s*$/u, "").trim();
      if (!label || label.length > 40) return null;
      rows.push({ label, nodes: line.slice(line.indexOf(head) + 1) });
    }
    return rows.length >= 2 ? rows : null;
  }
}

if (!customElements.get("tearstones-jewelry-description")) {
  customElements.define("tearstones-jewelry-description", TearstonesJewelryDescription);
}
